import { Question } from '@psyomics/surveyjs-core';

import { Answers, SupportedAnswerFormat, Survey } from '../../survey-types';
import { createTwoLevelCache } from '../cache';

const EXAMPLE_ANSWER = 'Example Answer';
const EXAMPLE_COMMENT = 'This is an example comment response with detailed feedback.';
const EXAMPLE_EXPRESSION_VALUE = 10;

const parseExampleAnswerJsonField = (exampleAnswerJson?: string) => {
  if (!exampleAnswerJson) return {};

  try {
    return JSON.parse(exampleAnswerJson);
  } catch {
    return {};
  }
};

const generateExampleAnswer = (question: Question) => {
  const type = question.getType();

  switch (type) {
    case 'checkbox':
      return [EXAMPLE_ANSWER, EXAMPLE_ANSWER];
    case 'comment':
      return EXAMPLE_COMMENT;
    case 'dropdown':
    case 'radiogroup':
      if (question.choices?.length > 0) {
        const [firstChoice] = question.choices;
        return firstChoice.value;
      }
      return EXAMPLE_ANSWER;
    case 'text':
      return EXAMPLE_ANSWER;
    case 'expression':
      return EXAMPLE_EXPRESSION_VALUE;
    default:
      return `Question type ${question.type} unsupported: ${EXAMPLE_ANSWER}`;
  }
};

const generateAnswersForQuestions = (questions: Question[]) => {
  return questions.reduce<Answers>((acc, question) => {
    return {
      ...acc,
      [question.name]: generateExampleAnswer(question),
    };
  }, {});
};

const CACHE_LIMIT = 10;
const answerCache = createTwoLevelCache<Survey, string, Answers>({
  level2Limit: CACHE_LIMIT,
});

const getAnswersForSurvey = (survey: Survey, answers?: SupportedAnswerFormat, overrides?: Answers): Answers => {
  if (answers?.[survey.respondentType]) {
    return answers[survey.respondentType];
  }

  const overridesKey = overrides ? JSON.stringify(overrides) : '';
  const cached = answerCache.get(survey, overridesKey);

  if (cached) {
    return cached;
  }

  const result = {
    ...generateAnswersForQuestions(survey.model.getAllQuestions()),
    ...overrides,
  };

  answerCache.set(survey, overridesKey, result);
  return result;
};

export {
  EXAMPLE_ANSWER,
  EXAMPLE_COMMENT,
  EXAMPLE_EXPRESSION_VALUE,
  generateAnswersForQuestions,
  generateExampleAnswer,
  getAnswersForSurvey,
  parseExampleAnswerJsonField,
};
